import { useCallback, useState } from 'react';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import type { KnowledgeBaseDocument } from '../../../types/knowledge';
import type {
  DocumentListOptions,
  WithResourceId,
} from '../../../types/knowledge';
import type { OperationProgress } from '../../../utils/operationProgress';
import { getDocumentLabel } from '../documentActionEligibility';
import { useDeleteKnowledgeDocumentMutation } from '../mutations/useKnowledgeDocumentMutations';
import { useKnowledgeDatabaseSharedStore } from '../stores/useKnowledgeDatabaseSharedStore';
import {
  mutateOnePromise,
  runBulkDeleteDocuments,
} from './documentBulkMutationHelpers';
import type { BulkConfirmState } from './useDocumentSelection';

type UseDocumentBulkDeleteActionsOptions = {
  databaseId: string;
  canManageDocuments: boolean;
  listOptions: DocumentListOptions;
  clearSelection: () => void;
  bulkConfirm: BulkConfirmState;
  setBulkConfirm: (value: BulkConfirmState) => void;
  isBulkActionRunning: boolean;
  setIsBulkActionRunning: (value: boolean) => void;
};

export function useDocumentBulkDeleteActions({
  databaseId,
  canManageDocuments,
  listOptions,
  clearSelection,
  bulkConfirm,
  setBulkConfirm,
  isBulkActionRunning,
  setIsBulkActionRunning,
}: UseDocumentBulkDeleteActionsOptions) {
  const { translate } = useI18n();
  const deleteMutation = useDeleteKnowledgeDocumentMutation();
  const [bulkDeleteProgress, setBulkDeleteProgress] =
    useState<OperationProgress | null>(null);

  const pendingDelete = useKnowledgeDatabaseSharedStore(
    (state) => state.pendingDelete,
  );
  const setPendingDelete = useKnowledgeDatabaseSharedStore(
    (state) => state.setPendingDelete,
  );

  const requestDeleteDocuments = useCallback(
    (targets: Array<WithResourceId<KnowledgeBaseDocument>>) => {
      if (targets.length === 0 || !canManageDocuments) {
        return;
      }
      if (targets.length === 1) {
        const document = targets[0];
        if (!document) {
          return;
        }
        setPendingDelete({
          type: 'document',
          id: document.id,
          label: getDocumentLabel(document),
        });
        return;
      }
      setBulkConfirm({ type: 'delete', documents: targets });
    },
    [canManageDocuments, setBulkConfirm, setPendingDelete],
  );

  const confirmDeleteDocument = () => {
    if (
      !pendingDelete ||
      pendingDelete.type !== 'document' ||
      deleteMutation.isPending
    ) {
      return;
    }

    const deletion = pendingDelete;
    deleteMutation.mutate(
      {
        databaseId,
        documentId: deletion.id,
        listOptions,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('knowledge.deleteSuccessTitle'),
            description: translate(
              'knowledge.deleteDocumentSuccessDescription',
              {
                fileName: deletion.label ?? deletion.id,
              },
            ),
          });
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.deleteErrorTitle'),
            translate('knowledge.deleteErrorDescription'),
          );
        },
        onSettled: () => {
          setPendingDelete(null);
          clearSelection();
        },
      },
    );
  };

  const confirmBulkDeleteDocuments = useCallback(() => {
    if (bulkConfirm?.type !== 'delete' || isBulkActionRunning) {
      return;
    }

    const targets = bulkConfirm.documents;
    void (async () => {
      setIsBulkActionRunning(true);
      setBulkDeleteProgress({ completed: 0, total: targets.length });
      await runBulkDeleteDocuments({
        targets,
        translate,
        onProgress: setBulkDeleteProgress,
        mutateOne: (document) =>
          mutateOnePromise(
            deleteMutation,
            {
              databaseId,
              documentId: document.id,
              listOptions,
            },
            translate('knowledge.deleteErrorTitle'),
            translate('knowledge.deleteErrorDescription'),
          ),
      });
      setBulkDeleteProgress(null);
      setIsBulkActionRunning(false);
      setBulkConfirm(null);
      clearSelection();
    })();
  }, [
    bulkConfirm,
    clearSelection,
    databaseId,
    deleteMutation,
    isBulkActionRunning,
    listOptions,
    setBulkConfirm,
    setIsBulkActionRunning,
    translate,
  ]);

  return {
    pendingDelete,
    setPendingDelete,
    requestDeleteDocuments,
    confirmDeleteDocument,
    confirmBulkDeleteDocuments,
    isDeletingDocument: deleteMutation.isPending,
    bulkDeleteProgress,
  };
}
